import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  X, 
  ChevronRight, 
  ChevronLeft, 
  Truck, 
  FileText, 
  Users, 
  BarChart3, 
  Settings,
  ClipboardCheck,
  Sparkles
} from "lucide-react";
import { cn } from "@/lib/utils";

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  highlight?: string;
  position: "center" | "left" | "right";
}

const tourSteps: TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to TransportMate! 🚗",
    description: "Let's take a quick tour to help you get started. This will only take a minute and will show you the key features.",
    icon: <Sparkles className="w-8 h-8 text-primary" />,
    position: "center",
  },
  {
    id: "create-job",
    title: "Create Your First Job",
    description: "Click 'Create Job' to start a new vehicle transport. You can add customer details, vehicle info, and schedule pickups all in one place.",
    icon: <Truck className="w-8 h-8 text-primary" />,
    highlight: "create-job",
    position: "left",
  },
  {
    id: "appraisals",
    title: "Digital Vehicle Appraisals",
    description: "Capture photos and document vehicle condition at collection and delivery. Our AI can even detect damage automatically!",
    icon: <ClipboardCheck className="w-8 h-8 text-primary" />,
    position: "center",
  },
  {
    id: "invoicing",
    title: "Automatic Invoicing",
    description: "Once a job is complete, generate professional invoices with one click. They sync automatically with QuickBooks, Xero, or FreeAgent.",
    icon: <FileText className="w-8 h-8 text-primary" />,
    position: "right",
  },
  {
    id: "customers",
    title: "Customer Management",
    description: "Keep all your customer information organised. Send tracking links so customers can follow their vehicle in real-time.",
    icon: <Users className="w-8 h-8 text-primary" />,
    position: "left",
  },
  {
    id: "analytics",
    title: "Track Your Performance",
    description: "View analytics and insights about your business. See completed jobs, revenue trends, and driver performance at a glance.",
    icon: <BarChart3 className="w-8 h-8 text-primary" />,
    position: "center",
  },
  {
    id: "settings",
    title: "Customise Your Experience",
    description: "Head to Settings to add your company logo, set up email templates, configure auto-invoicing, and manage your team.",
    icon: <Settings className="w-8 h-8 text-primary" />,
    position: "right",
  },
  {
    id: "complete",
    title: "You're All Set! 🎉",
    description: "That's the basics! Explore the Help Guides anytime for detailed walkthroughs. Now let's create your first job!",
    icon: <Sparkles className="w-8 h-8 text-green-500" />,
    position: "center",
  },
];

interface DashboardProductTourProps {
  onComplete: () => void;
}

export const DashboardProductTour = ({ onComplete }: DashboardProductTourProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const step = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    setIsVisible(false);
    localStorage.setItem("transportmate_tour_completed", "true");
    onComplete();
  };

  if (!isVisible) return null;

  return (
    // Single fixed positioning context for the whole tour: the card is centred
    // here and never repositioned per step, so its centre point cannot move.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "max(1rem, env(safe-area-inset-left))",
        paddingRight: "max(1rem, env(safe-area-inset-right))",
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Tour Card — fixed width/max-width so step content cannot shift it */}
      <Card
        className={cn(
          "relative z-10 w-full max-w-md p-6 bg-card border-border shadow-2xl animate-in fade-in zoom-in-95 duration-300"
        )}
      >
    
        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-8 w-8 text-muted-foreground hover:text-foreground"
          onClick={handleSkip}
        >
          <X className="h-4 w-4" />
        </Button>

        {/* Progress indicator */}
        <div className="flex gap-1.5 mb-6">
          {tourSteps.map((_, index) => (
            <div
              key={index}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-colors duration-300",
                index <= currentStep ? "bg-primary" : "bg-muted"
              )}
            />
          ))}
        </div>

        {/* Content */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-4 rounded-2xl bg-primary/10">
              {step.icon}
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-xl font-bold text-foreground">{step.title}</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {step.description}
            </p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <div>
            {!isFirstStep && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrevious}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {!isLastStep && (
              <Button variant="ghost" size="sm" onClick={handleSkip}>
                Skip tour
              </Button>
            )}
            <Button onClick={handleNext} size="sm" className="gap-1">
              {isLastStep ? "Get Started" : "Next"}
              {!isLastStep && <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Step counter */}
        <p className="text-center text-xs text-muted-foreground mt-4">
          Step {currentStep + 1} of {tourSteps.length}
        </p>
      </Card>
    </div>
  );
};
